import './account-detail.scss'
import SelectPeriod from '../../components/select-period/select-period'
import {useAuth} from '../../contexts/auth'
import {usePeriod} from '../../contexts/period'
import {getAccountDetail} from '../../api/dogovor'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import notify from 'devextreme/ui/notify'
import LoadPanel from 'devextreme-react/load-panel'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faChartPie, faFileInvoiceDollar, faFileLines, faTable} from '@fortawesome/free-solid-svg-icons'
import {verifyAccess, dogHasParameterMenu} from '../../utils/admin.utils'
import AccessDenied from '../../components/access-denied/access-denied'
import ZagolovokFromMenu from '../../components/zagolovok/zagolovok'
import PivotGrid, {FieldChooser, Export} from 'devextreme-react/pivot-grid'
import DataGrid, {Column, Summary, TotalItem} from 'devextreme-react/data-grid'
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source'
import {Workbook} from 'exceljs'
import {exportDataGrid, exportPivotGrid} from 'devextreme/excel_exporter'
import {saveAs} from 'file-saver'
import PieChart, {
    Connector,
    Export as PieExport,
    Label,
    Legend,
    Series as PieSeries,
    Tooltip as PieTooltip,
} from 'devextreme-react/pie-chart'
import Chart, {
    ArgumentAxis,
    CommonSeriesSettings,
    Export as ChartExport,
    Legend as ChartLegend,
    Margin,
    Series,
    Size,
    Label as AxisLabel,
    Title,
    Tooltip,
    ValueAxis,
} from 'devextreme-react/chart'
import {Popup} from 'devextreme-react'
import {PopupTitleWithIcon} from '../../components/popup-title-with-icon/popup-title-with-icon'
import {useScreenSize} from '../../utils/media-query'
import {useHistory} from 'react-router-dom'
import {getHomeRoute} from '../../utils/navigation.utils'

const SECTIONS = [
    {id: 'detail', title: 'Детализация'},
    {id: 'pricing', title: 'Анализ ценообразования'},
    {id: 'pie-price', title: 'Диаграмма анализ цены'},
    {id: 'pie-cost', title: 'Диаграмма анализ стоимости'},
    {id: 'economy', title: 'Экономия потребителя'},
]

const numFormat = {
    type: 'fixedPoint',
    precision: 2,
}

const priceFormat = {
    type: 'fixedPoint',
    precision: 5,
}

function formatNum(value, precision = 2) {
    if (value == null || value === '' || Number.isNaN(Number(value))) return ''
    return Number(value).toLocaleString('ru-RU', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    })
}

function rowOrderMap(rows) {
    const order = new Map()
    for (const r of rows || []) {
        const sort = Number(r.rowSort)
        if (r.rowName == null || !Number.isFinite(sort)) continue
        const prev = order.get(r.rowName)
        if (prev == null || sort < prev) order.set(r.rowName, sort)
    }
    return order
}

function rowSortingMethod(rowOrder) {
    const key = (x) => String(x.value ?? x.text ?? '')
    return (a, b) => {
        const av = rowOrder.has(key(a)) ? rowOrder.get(key(a)) : 99
        const bv = rowOrder.has(key(b)) ? rowOrder.get(key(b)) : 99
        if (av !== bv) return av - bv
        return key(a).localeCompare(key(b), 'ru')
    }
}

function buildPivotFields(rowCaption, {
    withVoltage = true,
    withEconomy = false,
    volumeCaption = 'Объём, кВтч',
    rowOrder = null,
} = {}) {
    const fields = [
        {
            caption: rowCaption,
            dataField: 'rowName',
            area: 'row',
            width: 240,
            expanded: true,
            ...(rowOrder && rowOrder.size ? {sortingMethod: rowSortingMethod(rowOrder)} : {}),
        },
    ]
    if (withVoltage) {
        fields.push({
            caption: 'Уровень напряжения',
            dataField: 'voltage',
            area: 'row',
            width: 120,
            expanded: true,
        })
    }
    fields.push(
        {
            caption: 'Период',
            dataField: 'ymLabel',
            area: 'column',
            sortingMethod: (a, b) => {
                const parse = (t) => {
                    const [m, y] = String(t || '').split('.')
                    return Number(y) * 100 + Number(m)
                }
                return parse(a.value ?? a.text) - parse(b.value ?? b.text)
            },
        },
        {
            caption: volumeCaption,
            dataField: 'cust',
            dataType: 'number',
            summaryType: 'sum',
            format: numFormat,
            area: 'data',
        },
        {
            caption: 'Цена',
            dataField: 'price',
            dataType: 'number',
            summaryType: 'avg',
            format: priceFormat,
            area: 'data',
        },
        {
            caption: 'Стоимость без НДС, руб.',
            dataField: 'money',
            dataType: 'number',
            summaryType: 'sum',
            format: numFormat,
            area: 'data',
        },
    )
    if (withEconomy) {
        fields.push(
            {
                caption: 'Экономия по цене',
                dataField: 'economyPrice',
                dataType: 'number',
                summaryType: 'avg',
                format: priceFormat,
                area: 'data',
            },
            {
                caption: 'Экономия по стоимости, руб.',
                dataField: 'economyMoney',
                dataType: 'number',
                summaryType: 'sum',
                format: numFormat,
                area: 'data',
            },
        )
    }
    return fields
}

const ECONOMY_SECTIONS = ['Факт', 'По ценам ГП', 'Экономия']
const ECONOMY_VOLUME = 'Объём, кВтч, кВт'
const ECONOMY_MEASURES = [
    ECONOMY_VOLUME,
    'Цена',
    'Стоимость без НДС, руб.',
    'по цене',
    'по стоимости, руб.',
]
const ECONOMY_PRICE_MEASURES = new Set(['Цена', 'по цене'])

function periodSortingMethod(a, b) {
    const parse = (t) => {
        const [m, y] = String(t || '').split('.')
        return Number(y) * 100 + Number(m)
    }
    return parse(a.value ?? a.text) - parse(b.value ?? b.text)
}

function sumBy(items, key) {
    return items.reduce((s, r) => s + (Number(r[key]) || 0), 0)
}

function maxBy(items, key) {
    let max = 0
    for (const r of items || []) {
        const v = Number(r[key]) || 0
        if (v > max) max = v
    }
    return max
}

function priceOf(money, qty) {
    if (!qty) return null
    return Math.round((money / qty) * 1e5) / 1e5
}

/** Одна строка группы на период: если API ещё режет покупку по видам, объём не суммируем. */
function collapseGroupRows(rows) {
    const groups = new Map()
    for (const r of rows || []) {
        const id = `${r.rowName}\0${r.ymLabel}`
        if (!groups.has(id)) groups.set(id, [])
        groups.get(id).push(r)
    }
    const out = []
    for (const items of groups.values()) {
        if (items.length === 1) {
            out.push(items[0])
            continue
        }
        const cust = Math.max(...items.map((r) => Number(r.cust) || 0))
        const money = sumBy(items, 'money')
        out.push({
            ...items[0],
            detailName: null,
            cust,
            custKwh: sumBy(items, 'custKwh'),
            money,
            price: priceOf(money, cust),
        })
    }
    return out
}

function economyPriceDiff(gpPrice, factPrice) {
    if (gpPrice == null && factPrice == null) return null
    return (gpPrice || 0) - (factPrice || 0)
}

function economyGroupLabel(name) {
    const key = String(name || '').trim().toLowerCase()
    if (key === 'покупка' || key === 'транспортировка') return `${String(name).trim()}, в т.ч.:`
    return name
}

/** Фактическая и сетевая мощность: в объёме своё количество, в итог группы оно не входит. */
function isPowerDetail(row) {
    const sort = Number(row?.detailSort)
    return sort === 2 || sort === 3
}

/**
 * Покупка и транспортировка: группа и под ней фиксированные строки видов начисления.
 * Объём группы и электроэнергии — max кВт·ч. У мощности — её количество.
 */
function buildEconomyLines(rows) {
    const groups = new Map()
    for (const r of rows || []) {
        const id = `${r.rowName}\0${r.ymLabel}`
        if (!groups.has(id)) groups.set(id, [])
        groups.get(id).push(r)
    }
    const out = []
    for (const items of groups.values()) {
        const head = items[0]
        const rowSort = Number(head.rowSort) || 99
        const uirp = rowSort === 4
        const energyItems = items.filter((r) => !isPowerDetail(r))
        const factQty = maxBy(uirp ? items : energyItems, 'factCust')
        const gpQty = maxBy(uirp ? items : energyItems, 'cust')
        const factMoney = sumBy(items, 'factMoney')
        const gpMoney = sumBy(items, 'money')
        const factPrice = priceOf(factMoney, factQty)
        const gpPrice = priceOf(gpMoney, gpQty)
        const sortKey = String(rowSort).padStart(3, '0')
        out.push({
            ...head,
            detailName: null,
            factCust: factQty,
            factPrice,
            factMoney,
            cust: gpQty,
            price: gpPrice,
            money: gpMoney,
            economyPrice: economyPriceDiff(gpPrice, factPrice),
            economyMoney: gpMoney - factMoney,
            isChild: false,
            lineKey: `p|${sortKey}|${head.rowName}`,
            lineLabel: economyGroupLabel(head.rowName),
            lineSort: rowSort * 1000,
        })
        const byDetail = new Map()
        for (const d of items) {
            if (!d.detailName || !String(d.detailName).trim()) continue
            if (!byDetail.has(d.detailName)) byDetail.set(d.detailName, [])
            byDetail.get(d.detailName).push(d)
        }
        const details = [...byDetail.entries()].sort((a, b) => {
            const as = Number(a[1][0].detailSort) || 9
            const bs = Number(b[1][0].detailSort) || 9
            if (as !== bs) return as - bs
            return String(a[0]).localeCompare(String(b[0]), 'ru')
        })
        for (const [name, dItems] of details) {
            const dSort = Number(dItems[0].detailSort) || 9
            const power = isPowerDetail(dItems[0])
            const dFactQty = power ? maxBy(dItems, 'factCust') : factQty
            const dGpQty = power ? maxBy(dItems, 'cust') : gpQty
            const dFactMoney = sumBy(dItems, 'factMoney')
            const dGpMoney = sumBy(dItems, 'money')
            const dFactPrice = priceOf(dFactMoney, dFactQty)
            const dGpPrice = priceOf(dGpMoney, dGpQty)
            out.push({
                ...dItems[0],
                factCust: dFactQty,
                factPrice: dFactPrice,
                factMoney: dFactMoney,
                cust: dGpQty,
                price: dGpPrice,
                money: dGpMoney,
                economyPrice: economyPriceDiff(dGpPrice, dFactPrice),
                economyMoney: dGpMoney - dFactMoney,
                isChild: true,
                lineKey: `c|${sortKey}|${head.rowName}|${dSort}|${name}`,
                lineLabel: name,
                lineSort: rowSort * 1000 + dSort,
            })
        }
    }
    return out
}

/** Период → Факт / По ценам ГП / Экономия → показатели. Факт = kod_typevariant 0. */
function expandEconomyRows(rows) {
    const out = []
    for (const r of rows || []) {
        const base = {lineKey: r.lineKey, ymLabel: r.ymLabel, isChild: !!r.isChild}
        const add = (section, measure, value) => {
            out.push({
                ...base,
                section,
                measure,
                value: value == null || value === '' ? null : Number(value),
            })
        }
        add('Факт', ECONOMY_VOLUME, r.factCust)
        add('Факт', 'Цена', r.factPrice)
        add('Факт', 'Стоимость без НДС, руб.', r.factMoney)
        add('По ценам ГП', ECONOMY_VOLUME, r.cust)
        add('По ценам ГП', 'Цена', r.price)
        add('По ценам ГП', 'Стоимость без НДС, руб.', r.money)
        add('Экономия', 'по цене', r.economyPrice)
        add('Экономия', 'по стоимости, руб.', r.economyMoney)
    }
    return out
}

function buildEconomyFields(rowCaption, lineMeta) {
    const fields = [
        {
            caption: rowCaption,
            dataField: 'lineKey',
            area: 'row',
            width: 280,
            customizeText: (cell) => lineMeta.labels.get(cell.value) || '',
            sortingMethod: (a, b) => (lineMeta.order.get(a.value) ?? 999999) - (lineMeta.order.get(b.value) ?? 999999),
        },
    ]
    fields.push(
        {
            caption: 'Период',
            dataField: 'ymLabel',
            area: 'column',
            areaIndex: 0,
            expanded: true,
            showTotals: false,
            sortingMethod: periodSortingMethod,
        },
        {
            dataField: 'section',
            area: 'column',
            areaIndex: 1,
            expanded: true,
            showTotals: false,
            sortingMethod: (a, b) => ECONOMY_SECTIONS.indexOf(a.value) - ECONOMY_SECTIONS.indexOf(b.value),
        },
        {
            dataField: 'measure',
            area: 'column',
            areaIndex: 2,
            expanded: true,
            showTotals: false,
            sortingMethod: (a, b) => ECONOMY_MEASURES.indexOf(a.value) - ECONOMY_MEASURES.indexOf(b.value),
        },
        {
            dataField: 'value',
            dataType: 'number',
            summaryType: 'sum',
            format: numFormat,
            area: 'data',
        },
    )
    return fields
}

function AccountDetailPivot({
    data,
    rowCaption,
    fileName,
    ofzByYm,
    onOfzClick,
    withVoltage = true,
    withEconomy = false,
    nestedEconomy = false,
    volumeCaption = 'Объём, кВтч',
    /** Итого по объёму детализации — сумма кВт·ч. В ценообразовании объём — max. */
    sumVolumeGrandTotal = true,
    /** Объём строки и итога — max(cust), одно и то же количество в группе. */
    volumeAsMax = false,
}) {
    const rowOrder = useMemo(() => rowOrderMap(data), [data])
    const economyLines = useMemo(() => (nestedEconomy ? buildEconomyLines(data) : []), [data, nestedEconomy])
    const lineMeta = useMemo(() => {
        const labels = new Map()
        const order = new Map()
        for (const r of economyLines) {
            labels.set(r.lineKey, r.lineLabel)
            order.set(r.lineKey, r.lineSort)
        }
        return {labels, order}
    }, [economyLines])
    const dataSource = useMemo(() => new PivotGridDataSource({
        fields: nestedEconomy
            ? buildEconomyFields(rowCaption, lineMeta)
            : buildPivotFields(rowCaption, {withVoltage, withEconomy, volumeCaption, rowOrder}),
        store: nestedEconomy ? expandEconomyRows(economyLines) : (data || []),
    }), [data, rowCaption, withVoltage, withEconomy, volumeCaption, nestedEconomy, rowOrder, economyLines, lineMeta])

    const onExporting = (e) => {
        const workbook = new Workbook()
        const worksheet = workbook.addWorksheet(fileName)
        exportPivotGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], {type: 'application/octet-stream'}), `${fileName}.xlsx`)
            })
        })
    }

    const onCellPrepared = (e) => {
        if (nestedEconomy && e.area === 'data' && e.cellElement && e.cell) {
            const path = e.cell.columnPath || []
            const measure = [...path].reverse().find((p) => ECONOMY_MEASURES.includes(p))
            const section = path.find((p) => ECONOMY_SECTIONS.includes(p))
            const ymLabel = path.find((p) => /^\d{2}\.\d{4}$/.test(String(p)))
            const isPrice = ECONOMY_PRICE_MEASURES.has(measure)
            const isEconomy = measure === 'по цене' || measure === 'по стоимости, руб.'
            if (isEconomy) {
                e.cellElement.classList.add('economy')
            }
            const isGrand = e.cell.rowType === 'GT'
            if (isGrand) {
                const parents = economyLines.filter((r) => !r.isChild && (!ymLabel || r.ymLabel === ymLabel))
                const volumeParents = parents.filter((r) => Number(r.rowSort) !== 4)
                const custKey = section === 'Факт' ? 'factCust' : 'cust'
                const moneyKey = section === 'Факт' ? 'factMoney' : 'money'
                const qty = maxBy(volumeParents, custKey)
                const money = sumBy(parents, moneyKey)
                if (measure === 'по цене') {
                    e.cellElement.innerText = ''
                    return
                }
                if (measure === ECONOMY_VOLUME) {
                    e.cellElement.innerText = formatNum(qty, 2)
                    return
                }
                if (measure === 'Цена') {
                    e.cellElement.innerText = qty ? formatNum(priceOf(money, qty), 5) : ''
                    return
                }
                if (measure === 'Стоимость без НДС, руб.') {
                    e.cellElement.innerText = formatNum(money, 2)
                    return
                }
                if (measure === 'по стоимости, руб.') {
                    e.cellElement.innerText = formatNum(sumBy(parents, 'economyMoney'), 2)
                    e.cellElement.classList.add('economy-total')
                    return
                }
            } else if (e.cell.value != null && e.cell.value !== '') {
                e.cellElement.innerText = formatNum(e.cell.value, isPrice ? 5 : 2)
            }
        } else if (e.area === 'data' && e.cellElement) {
            const idx = e.cell?.dataIndex
            if (withEconomy && (idx === 3 || idx === 4)) {
                e.cellElement.classList.add('economy')
            }
            if (volumeAsMax && idx === 0 && e.cell?.rowType !== 'GT') {
                const rowName = (e.cell.rowPath || [])[0]
                const rowYm = (e.cell.columnPath || [])[0]
                const subset = (data || []).filter((r) =>
                    (!rowName || r.rowName === rowName) && (!rowYm || r.ymLabel === rowYm)
                )
                e.cellElement.innerText = formatNum(maxBy(subset, 'cust'), 2)
                return
            }
            if (e.cell?.rowType === 'GT') {
                // 0 объём, 1 цена, 2 стоимость, [3 экон.цена, 4 экон.стоимость]
                const ymLabel = (e.cell.columnPath || [])[0]
                const subset = (data || []).filter((r) => !ymLabel || r.ymLabel === ymLabel)
                if (idx === 1) {
                    const money = subset.reduce((s, r) => s + (Number(r.money) || 0), 0)
                    const qty = volumeAsMax
                        ? maxBy(subset, 'cust')
                        : sumVolumeGrandTotal
                            ? subset.reduce((s, r) => s + (Number(r.custKwh) || 0), 0)
                            : subset.reduce((s, r) => s + (Number(r.cust) || 0), 0)
                    e.cellElement.innerText = qty ? formatNum(Math.round((money / qty) * 1e5) / 1e5, 5) : ''
                    return
                }
                if (withEconomy && idx === 3) {
                    e.cellElement.innerText = ''
                    return
                }
                if (idx === 0) {
                    const qty = volumeAsMax
                        ? maxBy(subset, 'cust')
                        : sumVolumeGrandTotal
                            ? subset.reduce((s, r) => s + (Number(r.custKwh) || 0), 0)
                            : maxBy(subset, 'cust')
                    e.cellElement.innerText = formatNum(qty, 2)
                    return
                }
                if (withEconomy && idx === 4) {
                    e.cellElement.classList.add('economy-total')
                }
            }
        }
        if (nestedEconomy && e.area === 'row' && e.cellElement) {
            const lineKey = String((e.cell?.path || [])[0] || '')
            if (lineKey.startsWith('p|')) e.cellElement.classList.add('economy-group')
            if (lineKey.startsWith('c|')) e.cellElement.classList.add('economy-child')
        }
        if (nestedEconomy && e.area === 'column' && e.cellElement) {
            const columnPath = e.cell?.path || []
            if (e.cell?.text === 'Экономия' || columnPath.includes('Экономия')) {
                e.cellElement.classList.add('economy-header')
            }
            e.cellElement.classList.remove('dx-pivotgrid-expanded', 'dx-pivotgrid-collapsed')
            e.cellElement.querySelectorAll('.dx-expand-icon-container').forEach((node) => node.remove())
        }
        // ОФЦ — только заголовок периода (MM.yyyy), не меры и не строки
        if (e.area !== 'column' || !e.cell || e.cell.type !== 'D') return
        if ((e.cell.path || []).length !== 1) return
        const ymLabel = e.cell.text || e.cell.path[0]
        if (!ymLabel || !/^\d{2}\.\d{4}$/.test(String(ymLabel))) return
        const [m, y] = String(ymLabel).split('.')
        const ymKey = `${y}.${m}`
        const kodSf = ofzByYm && ofzByYm[ymKey]
        if (!kodSf) return
        if (e.cellElement.querySelector('.ofz-header-btn')) return

        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'ofz-header-btn dx-icon dx-icon-docxfile'
        btn.title = 'Открыть ОФЦ'
        btn.setAttribute('aria-label', 'ОФЦ')
        btn.onclick = (ev) => {
            ev.preventDefault()
            ev.stopPropagation()
            onOfzClick(kodSf)
        }
        e.cellElement.appendChild(btn)
    }

    const onCellClick = (e) => {
        if (nestedEconomy && e.area === 'column' && e.cell && e.cell.expanded != null) {
            e.cancel = true
        }
    }

    return (
        <PivotGrid
            dataSource={dataSource}
            allowSortingBySummary={false}
            allowSorting={true}
            allowFiltering={true}
            showBorders={true}
            showColumnTotals={false}
            showColumnGrandTotals={false}
            showRowTotals={false}
            showRowGrandTotals={true}
            texts={{grandTotal: 'Итого', total: '{0}'}}
            onExporting={onExporting}
            onCellPrepared={onCellPrepared}
            onCellClick={onCellClick}
        >
            <FieldChooser enabled={false}/>
            <Export enabled={true} fileName={fileName}/>
        </PivotGrid>
    )
}

const MONTHS_RU = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

function vatPeriodLabel(ym) {
    const [y, m] = Number(ym).toFixed(2).split('.')
    return `${MONTHS_RU[Number(m) - 1] || m} ${y}`
}

function formatTax(value) {
    if (value == null || value === '' || Number.isNaN(Number(value))) return ''
    return Number(value).toLocaleString('ru-RU', {maximumFractionDigits: 2})
}

/** Табличная часть макета detal_nach: одна строка на период, объём в кВтч, цена как в итоге. */
function AccountDetailVatTable({rows, fileName}) {
    const data = rows || []
    const gridRef = useRef(null)
    const fileNameRef = useRef(fileName)
    fileNameRef.current = fileName
    const kwhQty = data.reduce((s, r) => s + (Number(r.edizm) === 4 ? (Number(r.cust) || 0) : 0), 0)
    const moneySum = data.reduce((s, r) => s + (Number(r.money) || 0), 0)
    const avgTariff = kwhQty ? Math.round((moneySum / kwhQty) * 1e5) / 1e5 : null
    const totalText = (precision) => (e) => formatNum(e.value, precision)

    const exportToExcel = () => {
        const component = gridRef.current?.instance
        if (!component) return
        const workbook = new Workbook()
        const worksheet = workbook.addWorksheet('Начисления')
        const thin = {style: 'thin', color: {argb: 'FF000000'}}
        const gridBorder = {top: thin, left: thin, bottom: thin, right: thin}
        exportDataGrid({
            component,
            worksheet,
            autoFilterEnabled: true,
            customizeCell: ({excelCell}) => {
                excelCell.border = gridBorder
            },
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], {type: 'application/octet-stream'}), `${fileNameRef.current}.xlsx`)
            })
        })
    }

    const onCellPrepared = (e) => {
        if (e.rowType !== 'header' || e.columnIndex !== 0 || !e.cellElement) return
        if (e.cellElement.querySelector('.vat-export-btn')) return
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'vat-export-btn'
        btn.title = 'Экспорт в Excel'
        btn.setAttribute('aria-label', 'Экспорт в Excel')
        btn.innerHTML = '<i class="dx-icon dx-icon-xlsxfile"></i>'
        btn.onclick = (ev) => {
            ev.preventDefault()
            ev.stopPropagation()
            exportToExcel()
        }
        e.cellElement.insertBefore(btn, e.cellElement.firstChild)
    }

    return (
        <div className="account-detail-vat">
            <DataGrid
                ref={gridRef}
                dataSource={data}
                showBorders={true}
                columnAutoWidth={true}
                wordWrapEnabled={true}
                noDataText="Нет данных за выбранный период"
                onCellPrepared={onCellPrepared}
            >
                <Column dataField="rowName" caption="Наименование" minWidth={180}/>
                <Column dataField="periodLabel" caption="Период" width={140}/>
                <Column dataField="edizmName" caption="Ед. изм." width={90}/>
                <Column dataField="cust" caption="Количество" dataType="number" format={numFormat} width={120}/>
                <Column dataField="price" caption="Цена, руб/кВтч" dataType="number" format={priceFormat} width={130}/>
                <Column dataField="money" caption="Стоимость, руб" dataType="number" format={numFormat} width={130}/>
                <Column dataField="tax" caption="НДС, %" dataType="number" customizeText={(c) => formatTax(c.value)} width={80}/>
                <Column dataField="nds" caption="Сумма НДС, руб" dataType="number" format={numFormat} width={130}/>
                <Column dataField="nachisl" caption="Всего стоимость с НДС, руб" dataType="number" format={numFormat} width={180}/>
                <Summary>
                    <TotalItem column="rowName" summaryType="count" customizeText={() => 'Итого'}/>
                    <TotalItem column="cust" summaryType="sum" customizeText={() => formatNum(kwhQty, 2)}/>
                    <TotalItem column="price" summaryType="avg" customizeText={() => avgTariff == null ? '' : formatNum(avgTariff, 5)}/>
                    <TotalItem column="money" summaryType="sum" customizeText={totalText(2)}/>
                    <TotalItem column="nds" summaryType="sum" customizeText={totalText(2)}/>
                    <TotalItem column="nachisl" summaryType="sum" customizeText={totalText(2)}/>
                </Summary>
            </DataGrid>
        </div>
    )
}

function AccountDetailPie({data}) {
    const customizeLabel = (pointInfo) =>
        `${(Number(pointInfo.percent) * 100).toFixed(2)}%`
    const customizeTooltip = (pointInfo) => ({
        text: `${pointInfo.argumentText}: ${Number(pointInfo.value).toLocaleString('ru-RU', {
            maximumFractionDigits: 5,
        })} (${(Number(pointInfo.percent) * 100).toFixed(2)}%)`,
    })

    if (!data || data.length === 0) {
        return <div className="account-detail-empty">Нет данных за выбранный период</div>
    }

    return (
        <PieChart
            dataSource={data}
            type="doughnut"
            palette="Soft Pastel"
            resolveLabelOverlapping="shift"
        >
            <PieSeries argumentField="name" valueField="value">
                <Label visible={true} customizeText={customizeLabel}>
                    <Connector visible={true} width={1}/>
                </Label>
            </PieSeries>
            <Legend
                verticalAlignment="bottom"
                horizontalAlignment="center"
                itemTextPosition="right"
            />
            <PieTooltip enabled={true} customizeTooltip={customizeTooltip}/>
            <PieExport enabled={true}/>
        </PieChart>
    )
}

const TOTAL_MONEY_FIELD = 'Итого, руб'
const TOTAL_PRICE_FIELD = 'Итого'

function collectSeriesNames(rows) {
    const seriesNames = []
    const seen = new Set()
    for (const r of rows || []) {
        const name = r.rowName
        if (!name || seen.has(name)) continue
        seen.add(name)
        seriesNames.push(name)
    }
    return seriesNames
}

/** Столбцы по группам + итоговый столбик в каждом месяце (valueKey: money | price). */
function buildStructureMonthChart(tableRows, {valueKey = 'money', totalField = TOTAL_MONEY_FIELD} = {}) {
    const rows = tableRows || []
    const seriesNames = collectSeriesNames(rows)
    const byYm = new Map()

    for (const r of rows) {
        const label = r.ymLabel
        if (!label) continue
        if (!byYm.has(label)) {
            const point = {
                ymLabel: label,
                ym: Number(r.ym) || 0,
                [totalField]: 0,
            }
            for (const name of seriesNames) point[name] = 0
            byYm.set(label, point)
        }
        const point = byYm.get(label)
        const name = r.rowName
        if (valueKey === 'price') {
            if (!point._acc) point._acc = {}
            if (name) {
                if (!point._acc[name]) point._acc[name] = {money: 0, kwh: 0}
                point._acc[name].money += Number(r.money) || 0
                point._acc[name].kwh += Number(r.custKwh) || 0
            }
            if (!point._acc._total) point._acc._total = {money: 0, kwh: 0}
            point._acc._total.money += Number(r.money) || 0
            const totalKwh = Number(r.custKwh) || 0
            if (totalKwh > point._acc._total.kwh) point._acc._total.kwh = totalKwh
            continue
        }
        const val = Number(r[valueKey]) || 0
        if (name) point[name] = (Number(point[name]) || 0) + val
        point[totalField] = (Number(point[totalField]) || 0) + val
    }

    for (const point of byYm.values()) {
        if (!point._acc) continue
        const priceOf = (acc) => acc && acc.kwh ? Math.round((acc.money / acc.kwh) * 1e5) / 1e5 : 0
        for (const name of seriesNames) point[name] = priceOf(point._acc[name])
        point[totalField] = priceOf(point._acc._total)
        delete point._acc
    }

    return {
        data: Array.from(byYm.values()).sort((a, b) => a.ym - b.ym),
        seriesNames,
        totalField,
    }
}

function buildMonthCostChart(tableRows, chartRows) {
    const base = buildStructureMonthChart(tableRows, {
        valueKey: 'money',
        totalField: TOTAL_MONEY_FIELD,
    })
    for (const point of base.data) {
        point.custKwh = 0
    }
    for (const r of tableRows || []) {
        const point = base.data.find((p) => p.ymLabel === r.ymLabel)
        if (point) point.custKwh += Number(r.custKwh) || 0
    }
    for (const r of chartRows || []) {
        const point = base.data.find((p) => p.ymLabel === r.ymLabel)
        if (!point) continue
        const fromApi = Number(r.custKwh) || 0
        if (fromApi && !(Number(point.custKwh) > 0)) point.custKwh = fromApi
    }
    return base
}

function AccountDetailMonthChart({chart, showConsumption = true, valueAxisTitle = 'руб.', valueDigits = 2}) {
    const seriesNames = chart?.seriesNames || []
    const totalField = chart?.totalField || TOTAL_MONEY_FIELD
    const data = (chart?.data || []).map((point) => {
        const next = {...point}
        for (const name of [...seriesNames, totalField]) {
            if (!(Number(next[name]) > 0)) next[name] = null
        }
        return next
    })

    if (!data.length || !seriesNames.length) {
        return <div className="account-detail-empty">Нет данных для диаграммы</div>
    }

    const customizeTooltip = (info) => {
        const points = (info.points || []).map((p) =>
            `${p.seriesName}: ${Number(p.value).toLocaleString('ru-RU', {maximumFractionDigits: valueDigits, minimumFractionDigits: valueDigits === 2 ? 0 : 2})}`
        ).join('<br/>')
        return {html: `<div><b>${info.argumentText}</b><br/>${points}</div>`}
    }

    return (
        <div className="account-detail-month-chart">
            <Chart dataSource={data} palette="Soft Pastel" synchronizeMultiAxes={false}>
                <Size height={showConsumption ? 400 : 360}/>
                <Margin top={12} bottom={8} left={8} right={8}/>
                <CommonSeriesSettings argumentField="ymLabel" type="bar"/>
                {seriesNames.map((name) => (
                    <Series
                        key={name}
                        valueField={name}
                        name={name}
                        axis="moneyAxis"
                    />
                ))}
                <Series
                    valueField={totalField}
                    name={totalField}
                    axis="moneyAxis"
                />
                {showConsumption && (
                    <Series
                        valueField="custKwh"
                        name="Потребление, кВт·ч"
                        type="spline"
                        axis="custAxis"
                    />
                )}
                <ValueAxis name="moneyAxis" position="left" type="logarithmic" logarithmBase={2}>
                    <Title text={`${valueAxisTitle}, лог. шкала 2`}/>
                    <AxisLabel format={{type: 'fixedPoint', precision: valueDigits}}/>
                </ValueAxis>
                {showConsumption && (
                    <ValueAxis name="custAxis" position="right">
                        <Title text="кВт·ч"/>
                    </ValueAxis>
                )}
                <ArgumentAxis/>
                <ChartLegend
                    verticalAlignment="bottom"
                    horizontalAlignment="center"
                />
                <Tooltip enabled={true} shared={true} customizeTooltip={customizeTooltip}/>
                <ChartExport enabled={true}/>
            </Chart>
        </div>
    )
}

function ToTopLink({onClick}) {
    return (
        <div className="account-detail-to-top-wrap">
            <button type="button" className="account-detail-to-top" onClick={onClick}>
                наверх
            </button>
        </div>
    )
}

const AccountDetail = () => {
    const {selectedDog, dogovors} = useAuth()
    const history = useHistory()
    const {periodStart, periodEnd} = usePeriod()
    const [loading, setLoading] = useState(false)
    const [visiblePage, setVisiblePage] = useState(true)
    const [vatRows, setVatRows] = useState([])
    const [detailRows, setDetailRows] = useState([])
    const [sostRows, setSostRows] = useState([])
    const [economyRows, setEconomyRows] = useState([])
    const [chartDetail, setChartDetail] = useState([])
    const [chartPricing, setChartPricing] = useState([])
    const [piePrice, setPiePrice] = useState([])
    const [pieCost, setPieCost] = useState([])
    const [ofzByYm, setOfzByYm] = useState({})
    const [activeSection, setActiveSection] = useState(SECTIONS[0].id)
    const [visibleIframe, setVisibleIframe] = useState(false)
    const navLockRef = useRef(false)
    const {isXSmall} = useScreenSize()

    useEffect(() => {
        if (!selectedDog) return
        // Только НЭСК (parameter_menu 0); иначе — на домашнюю, как у других форм
        if (!dogHasParameterMenu(selectedDog, '0')) {
            history.replace(getHomeRoute(dogovors, selectedDog))
            return
        }
        const pageRoles = [3]
        if (!verifyAccess(pageRoles, selectedDog)) {
            setVisiblePage(false)
            return
        }
        ;(async function () {
            setVisiblePage(true)
            if (!selectedDog?.kodDog || !periodStart || !periodEnd) return
            setLoading(true)
            try {
                const res = await getAccountDetail(selectedDog.kodDog, periodStart, periodEnd)
                if (res.isOk) {
                    setVatRows((res.data.vatRows || []).map((r) => ({
                        ...r,
                        periodLabel: vatPeriodLabel(r.ym),
                    })))
                    setDetailRows(res.data.detailRows || [])
                    setSostRows(res.data.sostRows || [])
                    setEconomyRows(res.data.economyRows || [])
                    setChartDetail(res.data.chartDetail || [])
                    setChartPricing(res.data.chartPricing || [])
                    setPiePrice(res.data.piePrice || [])
                    setPieCost(res.data.pieCost || [])
                    setOfzByYm(res.data.ofzByYm || {})
                } else {
                    notify(res.message, 'error', 5000)
                }
            } catch (e) {
                notify(e.message, 'error', 5000)
            } finally {
                setLoading(false)
            }
        })()
    }, [selectedDog, periodStart, periodEnd, dogovors, history])

    useEffect(() => {
        const observers = []
        const ratios = {}

        const updateActive = () => {
            if (navLockRef.current) return
            let bestId = SECTIONS[0].id
            let bestRatio = 0
            for (const s of SECTIONS) {
                const r = ratios[s.id] || 0
                if (r > bestRatio) {
                    bestRatio = r
                    bestId = s.id
                }
            }
            if (bestRatio > 0) setActiveSection(bestId)
        }

        for (const s of SECTIONS) {
            const el = document.getElementById(`section-${s.id}`)
            if (!el) continue
            const obs = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        ratios[s.id] = entry.intersectionRatio
                    }
                    updateActive()
                },
                {root: null, rootMargin: '-20% 0px -55% 0px', threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]}
            )
            obs.observe(el)
            observers.push(obs)
        }
        return () => observers.forEach((o) => o.disconnect())
    }, [detailRows, sostRows, piePrice, pieCost, loading])

    const scrollToSection = useCallback((id) => {
        const el = document.getElementById(`section-${id}`)
        if (!el) return
        navLockRef.current = true
        setActiveSection(id)
        el.scrollIntoView({behavior: 'smooth', block: 'start'})
        setTimeout(() => {
            navLockRef.current = false
        }, 700)
    }, [])

    const scrollToTop = useCallback(() => {
        scrollToSection('detail')
    }, [scrollToSection])

    const showOfz = useCallback(async (kodSf) => {
        if (!kodSf) {
            notify('Нет счёта-фактуры (vid_real=2) за период для ОФЦ', 'warning', 4000)
            return
        }
        let url = window.location.origin
        if (url.includes('localhost')) url = 'http://localhost:3100'
        const src = `${url}/api/report/show?name=OFZ&id=${kodSf}`
        if (isXSmall) {
            window.open(src, '_blank')
            return
        }
        setVisibleIframe(true)
        await new Promise((r) => setTimeout(r, 100))
        const divContainerIframe = document.getElementById('divContainerIframeAccountDetail')
        if (!divContainerIframe) return
        while (divContainerIframe.firstChild) {
            divContainerIframe.removeChild(divContainerIframe.firstChild)
        }

        const loaderWrapper = document.createElement('div')
        loaderWrapper.style.position = 'absolute'
        loaderWrapper.style.top = '0'
        loaderWrapper.style.left = '0'
        loaderWrapper.style.width = '100%'
        loaderWrapper.style.height = '100%'
        loaderWrapper.style.display = 'flex'
        loaderWrapper.style.flexDirection = 'column'
        loaderWrapper.style.justifyContent = 'center'
        loaderWrapper.style.alignItems = 'center'
        loaderWrapper.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
        loaderWrapper.style.zIndex = '1000'

        const loaderGif = document.createElement('img')
        loaderGif.src = '/Loading_icon.webp'
        loaderGif.style.width = '200px'

        const loaderText = document.createElement('div')
        loaderText.textContent = 'Идет формирование документа...'
        loaderText.style.marginTop = '10px'
        loaderText.style.fontSize = '14px'
        loaderText.style.color = '#333'

        loaderWrapper.appendChild(loaderGif)
        loaderWrapper.appendChild(loaderText)

        const f = document.createElement('iframe')
        f.src = src
        f.className = 'iframe-findocs'
        divContainerIframe.append(f)
        divContainerIframe.appendChild(loaderWrapper)
        f.onload = function () {
            loaderWrapper.style.display = 'none'
            if (loaderWrapper.parentNode) {
                divContainerIframe.removeChild(loaderWrapper)
            }
        }
        f.onerror = function () {
            loaderWrapper.style.display = 'none'
            if (loaderWrapper.parentNode) {
                divContainerIframe.removeChild(loaderWrapper)
            }
        }
    }, [isXSmall])

    const closeIframe = () => {
        const divContainerIframe = document.getElementById('divContainerIframeAccountDetail')
        if (divContainerIframe?.getElementsByTagName('iframe')[0]) {
            divContainerIframe.removeChild(divContainerIframe.getElementsByTagName('iframe')[0])
        }
        while (divContainerIframe?.firstChild) {
            divContainerIframe.removeChild(divContainerIframe.firstChild)
        }
        setVisibleIframe(false)
    }

    const ndog = selectedDog?.ndog || ''

    const pricingRows = useMemo(() => collapseGroupRows(sostRows), [sostRows])

    const detailMonthChart = useMemo(
        () => buildMonthCostChart(detailRows, chartDetail),
        [detailRows, chartDetail]
    )
    const pricingMonthChart = useMemo(
        () => buildMonthCostChart(
            pricingRows.map((r) => ({...r, custKwh: 0})),
            chartPricing
        ),
        [pricingRows, chartPricing]
    )
    const priceStructureChart = useMemo(
        () => buildStructureMonthChart(pricingRows, {valueKey: 'price', totalField: TOTAL_PRICE_FIELD}),
        [pricingRows]
    )
    const costStructureChart = useMemo(
        () => buildStructureMonthChart(pricingRows, {valueKey: 'money', totalField: TOTAL_MONEY_FIELD}),
        [pricingRows]
    )

    return (
        <>{visiblePage ?
            <div className={'content-block dx-card responsive-paddings account-detail big-input'}>
                <FontAwesomeIcon className="icon-header-h3 main-color main-color-light" icon={faFileInvoiceDollar}/>
                <h3><strong><ZagolovokFromMenu/></strong></h3>

                <div className="row mt20 mb20" style={{alignItems: 'baseline'}}>
                    <SelectPeriod mode={'multiple'}/>
                </div>

                <nav className="account-detail-nav" aria-label="Разделы страницы">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            className={activeSection === s.id ? 'active' : ''}
                            onClick={() => scrollToSection(s.id)}
                        >
                            {s.title}
                        </button>
                    ))}
                </nav>

                <section id="section-detail" className="account-detail-section">
                    <h4>
                        <FontAwesomeIcon icon={faTable} className="main-color"/> Детализация
                    </h4>
                    <AccountDetailVatTable rows={vatRows} fileName={`Начисления ${ndog}`}/>
                    <AccountDetailPivot
                        data={detailRows}
                        rowCaption="Вид начисления"
                        fileName={`Детализация ${ndog}`}
                        ofzByYm={ofzByYm}
                        onOfzClick={showOfz}
                        withVoltage={true}
                        volumeCaption="Объём, кВтч, кВт"
                    />
                    <AccountDetailMonthChart chart={detailMonthChart}/>
                    <ToTopLink onClick={scrollToTop}/>
                </section>

                <section id="section-pricing" className="account-detail-section">
                    <h4>
                        <FontAwesomeIcon icon={faTable} className="main-color"/> Анализ ценообразования
                    </h4>
                    <AccountDetailPivot
                        data={pricingRows}
                        rowCaption="Группа составляющих"
                        fileName={`Анализ ценообразования ${ndog}`}
                        ofzByYm={ofzByYm}
                        onOfzClick={showOfz}
                        withVoltage={false}
                        volumeCaption="Объём, кВтч, кВт"
                        volumeAsMax={true}
                    />
                    <AccountDetailMonthChart chart={pricingMonthChart}/>
                    <ToTopLink onClick={scrollToTop}/>
                </section>

                <section id="section-pie-price" className="account-detail-section">
                    <h4>
                        <FontAwesomeIcon icon={faChartPie} className="main-color"/> Диаграмма анализ цены
                    </h4>
                    <div className="account-detail-charts-row">
                        <div className="account-detail-charts-col">
                            <div className="account-detail-charts-caption">Структура цены</div>
                            <AccountDetailPie data={piePrice}/>
                        </div>
                        <div className="account-detail-charts-col">
                            <div className="account-detail-charts-caption">Структура цены по месяцам</div>
                            <AccountDetailMonthChart
                                chart={priceStructureChart}
                                showConsumption={false}
                                valueAxisTitle="руб."
                                valueDigits={5}
                            />
                        </div>
                    </div>
                    <ToTopLink onClick={scrollToTop}/>
                </section>

                <section id="section-pie-cost" className="account-detail-section">
                    <h4>
                        <FontAwesomeIcon icon={faChartPie} className="main-color"/> Диаграмма анализ стоимости
                    </h4>
                    <div className="account-detail-charts-row">
                        <div className="account-detail-charts-col">
                            <div className="account-detail-charts-caption">Структура стоимости</div>
                            <AccountDetailPie data={pieCost}/>
                        </div>
                        <div className="account-detail-charts-col">
                            <div className="account-detail-charts-caption">Структура стоимости по месяцам</div>
                            <AccountDetailMonthChart
                                chart={costStructureChart}
                                showConsumption={false}
                                valueAxisTitle="руб."
                            />
                        </div>
                    </div>
                    <ToTopLink onClick={scrollToTop}/>
                </section>

                <section id="section-economy" className="account-detail-section">
                    <h4>Экономия потребителя</h4>
                    {economyRows.length === 0 ? (
                        <div className="account-detail-empty">Нет данных за выбранный период</div>
                    ) : (
                        // ОФЦ в «Экономии потребителя» не выводим: отчёт о фактической цене — только в начислениях и ценообразовании
                        <AccountDetailPivot
                            data={economyRows}
                            rowCaption="Группа составляющих"
                            fileName={`Экономия потребителя ${ndog}`}
                            withVoltage={false}
                            nestedEconomy={true}
                        />
                    )}
                    <ToTopLink onClick={scrollToTop}/>
                </section>

                <LoadPanel visible={loading}/>

                <Popup
                    visible={visibleIframe}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    onHiding={closeIframe}
                    showTitle={true}
                    maxWidth={1200}
                    maxHeight={'90%'}
                    height={'auto'}
                    titleRender={() => PopupTitleWithIcon(faFileLines, 'Отчет о фактической цене', closeIframe)}
                >
                    <div id="divContainerIframeAccountDetail" className="findocs"/>
                </Popup>
            </div>
            : <AccessDenied/>}</>
    )
}

export default AccountDetail
